/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, Search } from 'lucide-react';
import { MixDetails, MixRepository, BrandRepository, FlavorRepository, DbBrand, FlavorDetails } from '../repositories/LocalDatabase';
import { mapDbMixToShishaMix } from '../repositories/mapper';
import { ShishaMix } from '../types';
import MixCard from './MixCard';
import MixDetailModal from './MixDetailModal';
import { showHUD } from './NotificationHUD';
import Pagination from './Pagination';

export default function RecipeList() {
  const [mixes, setMixes] = useState<MixDetails[]>([]);
  const [sortBy, setSortBy] = useState<'rating' | 'name' | 'created_at'>(
    'created_at',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWindowStart, setPageWindowStart] = useState(1);
  const [selectedMix, setSelectedMix] = useState<ShishaMix | null>(null);
  const [selectedDbMix, setSelectedDbMix] = useState<MixDetails | null>(null);

  // Search states
  const [searchBrand, setSearchBrand] = useState('');
  const [searchFlavor, setSearchFlavor] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Autocomplete data
  const [allBrands, setAllBrands] = useState<DbBrand[]>([]);
  const [allFlavors, setAllFlavors] = useState<FlavorDetails[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [dbMixes, dbBrands, dbFlavors] = await Promise.all([
      MixRepository.getAll(),
      BrandRepository.getAll(),
      FlavorRepository.getAll(),
    ]);
    setMixes(dbMixes.filter(m => m.user_id === 'local-user'));
    setAllBrands(dbBrands);
    setAllFlavors(dbFlavors);
  };

  const handleFavorite = async (mixId: string) => {
    try {
      const newValue = await MixRepository.toggleFavorite(mixId);
      showHUD({
        type: 'success',
        message: newValue ? 'お気に入りに登録しました ♥' : 'お気に入りから外しました',
      });
      await loadData();
      // 選択中のmixのisFavoriteを更新
      if (selectedDbMix && selectedDbMix.id === mixId) {
        setSelectedDbMix((prev) => prev ? { ...prev, is_favorite: newValue } : prev);
      }
    } catch (e) {
      showHUD({ type: 'error', message: 'お気に入りの更新に失敗しました' });
    }
  };

  // Whenever sortBy changes, we might want to reset to page 1
  useEffect(() => {
    setCurrentPage(1);
    setPageWindowStart(1);
  }, [sortBy]);

  const filteredMixes = mixes.filter(m => {
    if (searchBrand && !m.flavors.some(f => f.brand_name.toLowerCase().includes(searchBrand.toLowerCase()))) return false;
    if (searchFlavor && !m.flavors.some(f => (f.flavor_name || f.custom_flavor_name || '').toLowerCase().includes(searchFlavor.toLowerCase()))) return false;
    if (searchDate && !m.created_at.startsWith(searchDate)) return false;
    return true;
  });

  const sortedMixes = [...filteredMixes].sort((a, b) => {
    if (sortBy === 'created_at') {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }
    if (sortBy === 'rating') {
      return (b.rating || 0) - (a.rating || 0);
    }
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name, 'ja');
    }
    return 0;
  });

  const itemsPerPage = 8;
  const totalPages = Math.ceil(sortedMixes.length / itemsPerPage);
  const paginatedMixes = sortedMixes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div
      style={{
        padding: '30px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
        animation: 'fadeIn 0.5s ease-out',
      }}
    >
      {/* Title */}
      <h1
        style={{
          fontSize: 'var(--font-size-h1)',
          fontFamily: 'var(--font-heading-ja)',
          color: 'var(--color-text)',
          marginBottom: '16px',
        }}
      >
        レシピ一覧
      </h1>

      {/* Controls: Filter & Sort */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginBottom: '24px',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Filter Toggle Button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: showFilters ? 'var(--color-primary)' : 'var(--color-surface)',
              border: showFilters ? '1px solid var(--color-primary)' : '1px solid rgba(231, 228, 223, 0.1)',
              padding: '9px 14px',
              borderRadius: '8px',
              color: showFilters ? '#fff' : 'var(--color-text)',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <Search size={16} />
            レシピ検索
          </button>

          {/* Sort Options */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <select
              value={sortBy}
              onChange={(e: any) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                padding: '10px 36px 10px 14px',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '14px',
                outline: 'none',
                appearance: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="created_at">作成日順</option>
              <option value="rating">評価順</option>
              <option value="name">名前順</option>
            </select>
            <ChevronDown
              size={16}
              style={{
                position: 'absolute',
                right: '12px',
                pointerEvents: 'none',
                opacity: 0.6,
              }}
            />
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div
            className="glass-panel"
            style={{
              padding: '16px',
              borderRadius: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              width: '100%',
              maxWidth: '600px',
              justifyContent: 'flex-start',
              animation: 'fadeIn 0.2s ease-out',
              backgroundColor: 'rgba(26, 28, 31, 0.6)',
              border: '1px solid rgba(231, 228, 223, 0.1)',
            }}
          >
            {/* Brand Dropdown */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '1 1 140px' }}>
              <select
                value={searchBrand}
                onChange={(e) => { setSearchBrand(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid rgba(231, 228, 223, 0.15)',
                  padding: '10px 32px 10px 12px',
                  borderRadius: '8px',
                  color: 'var(--color-text)',
                  outline: 'none',
                  appearance: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">ブランドを絞り込む</option>
                {allBrands.map(b => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
              </select>
              <ChevronDown
                size={16}
                style={{ position: 'absolute', right: '12px', pointerEvents: 'none', opacity: 0.6 }}
              />
            </div>

            {/* Flavor Dropdown */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '1 1 140px' }}>
              <select
                value={searchFlavor}
                onChange={(e) => { setSearchFlavor(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid rgba(231, 228, 223, 0.15)',
                  padding: '10px 32px 10px 12px',
                  borderRadius: '8px',
                  color: 'var(--color-text)',
                  outline: 'none',
                  appearance: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">フレーバーを絞り込む</option>
                {allFlavors
                  .filter(f => !searchBrand || f.brand_name.toLowerCase() === searchBrand.toLowerCase())
                  .map(f => (
                    <option key={f.id} value={f.name}>
                      {f.brand_name} - {f.name}
                    </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                style={{ position: 'absolute', right: '12px', pointerEvents: 'none', opacity: 0.6 }}
              />
            </div>
            <input
              type="date"
              value={searchDate}
              onChange={(e) => { setSearchDate(e.target.value); setCurrentPage(1); }}
              style={{
                flex: '0 1 160px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(231, 228, 223, 0.15)',
                backgroundColor: 'var(--color-background)',
                color: 'var(--color-text)',
                outline: 'none',
                colorScheme: 'dark',
              }}
            />
          </div>
        )}
      </div>

      {/* Card Grid */}
      {paginatedMixes.length === 0 ? (
        <div
          className="glass-panel"
          style={{ padding: '40px', textAlign: 'center', opacity: 0.8 }}
        >
          <p>レシピがありません。</p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {paginatedMixes.map((dbMix) => {
            const mix = mapDbMixToShishaMix(dbMix);
            return (
              <MixCard
                key={mix.id}
                mix={mix}
                onClick={() => {
                  setSelectedMix(mix);
                  setSelectedDbMix(dbMix);
                }}
              />
            );
          })}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageWindowStart={pageWindowStart}
        onPageWindowStartChange={setPageWindowStart}
      />

      {/* Popup */}
      {selectedMix && (
        <MixDetailModal
          mix={selectedMix}
          onClose={() => {
            setSelectedMix(null);
            setSelectedDbMix(null);
          }}
          onLike={() => {}}
          onBookmark={() => {}}
          onAddComment={() => {}}
          isLiked={false}
          isBookmarked={false}
          onFavorite={handleFavorite}
          isFavorite={selectedDbMix?.is_favorite ?? false}
        />
      )}
    </div>
  );
}
